// src/features/records/components/CalendarView/MonthView/index.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CalendarGrid from './CalendarGrid';
import { formatMonthYear, getWeekdayNames } from '../utils/dateHelpers';
import styles from '../CalendarView.module.css';
import gridStyles from './CalendarGrid.module.css';

const MonthView = ({ 
  records,
  userProfiles = {},
  currentMonth,
  onMonthChange,
  onTodayClick,
  onDayClick,
  onRecordClick,
  loading,
  profilesLoading = false
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const monthYearLabel = formatMonthYear(currentMonth, { locale }).full;
  const weekdayNames = getWeekdayNames({ locale });
  
  const handlePrevMonth = () => {
    onMonthChange(-1);
  };
  
  const handleNextMonth = () => {
    onMonthChange(1);
  };
  
  // Touch handling for swipe gestures
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);
  
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNextMonth();
    } else if (isRightSwipe) {
      handlePrevMonth();
    }
  };
  
  return (
    <div 
      className={styles.monthView}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.monthNavigation}>
            <button
              className={styles.navButton}
              onClick={handlePrevMonth}
              aria-label={t('records.views.calendar.navigation.previous_month_aria')}
              disabled={loading}
            >
              <ChevronLeft size={20} />
            </button>
            
            <h1
              key={monthYearLabel} // Force replacement to avoid Android WebView repaint bug
              className={styles.monthYear}
            >
              {monthYearLabel}
            </h1>
            
            <button
              className={styles.navButton}
              onClick={handleNextMonth}
              aria-label={t('records.views.calendar.navigation.next_month_aria')}
              disabled={loading}
            >
              <ChevronRight size={20} />
            </button>
          </div>
          
          <button
            className={styles.todayButton}
            onClick={onTodayClick}
            disabled={loading}
          >
            {t('records.time.today', 'Today')}
          </button>
        </div>
      </header>
      
      {/* Calendar Grid */}
      <div className={styles.calendarContainer}>
        {loading ? (
          <div
            className={gridStyles.calendarGrid}
            role="grid"
            aria-label={t('records.views.calendar.navigation.loading_grid_aria')}
          >
            {/* Static weekday headers */}
            <div className={gridStyles.weekdayRow} role="row">
              {weekdayNames.map((day) => (
                <div 
                  key={day} 
                  className={gridStyles.weekdayHeader}
                  role="columnheader"
                  aria-label={day}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Static calendar weeks placeholder */}
            {Array.from({ length: 6 }).map((_, weekIndex) => (
              <div key={weekIndex} className={gridStyles.weekRow} role="row">
                {Array.from({ length: 7 }).map((_, dayIndex) => (
                  <div 
                    key={`${weekIndex}-${dayIndex}`} 
                    className={gridStyles.calendarCell}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <CalendarGrid
            records={records}
            currentMonth={currentMonth}
            onDayClick={onDayClick}
            onRecordClick={onRecordClick}
            userProfiles={userProfiles}
            profilesLoading={profilesLoading}
          />
        )}
      </div>
    </div>
  );
};

export default MonthView;
